import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pill } from "../../../common/DataList";
import ModaleCorrection from "./ModaleCorrection";
import { etapesDecision, ligneAccompagnement, lignesCorrections, nomAccompagnement } from "./accompagnement";
import { voirSousOrientation } from "./sousOrientationApi";
import "./sous-orientation.scss";

const ICONES = { ok: "fa-check-circle", na: "fa-minus-circle", partiel: "fa-exclamation-circle", avenir: "fa-circle", caf: "fa-arrow-right" };

// ============================================================
// Encart « Accompagnement » de l'en-tête du dossier (CE-1370, maquette 11) :
// à qui l'allocataire est confié après la sous-orientation, pourquoi, et la
// correction pour qui peut corriger (l'API renvoie alors `choix`). Rien sans
// sous-orientation.
// ============================================================
const EncartAccompagnement = ({ allocataireId, nomAllocataire, onCorrigee = () => {} }) => {
  const [so, setSo] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);
  const [ouverte, setOuverte] = useState(false);
  const refCorriger = useRef(null);
  const monte = useRef(true);

  const charger = useCallback(() => {
    setChargement(true);
    setErreur(false);
    voirSousOrientation(allocataireId).then(
      (lue) => monte.current && setSo(lue),
      () => monte.current && setErreur(true)
    ).finally(() => monte.current && setChargement(false));
  }, [allocataireId]);

  useEffect(() => {
    monte.current = true;
    charger();
    return () => {
      monte.current = false;
    };
  }, [charger]);

  const fermer = () => {
    setOuverte(false);
    // Le focus revient au bouton qui a ouvert la modale.
    setTimeout(() => refCorriger.current && refCorriger.current.focus(), 0);
  };

  if (chargement) {
    return (
      <div className="accompagnement" aria-busy="true">
        <p className="accompagnement__chargement">Chargement de l'accompagnement…</p>
      </div>
    );
  }
  if (erreur) {
    return (
      <div className="accompagnement">
        <p className="accompagnement__erreur" role="alert">
          L'accompagnement n'a pas pu être chargé.{" "}
          <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={charger}>
            Réessayer
          </button>
        </p>
      </div>
    );
  }
  if (!so) return null;

  const corrections = lignesCorrections(so);
  return (
    <section className="accompagnement" aria-labelledby="accompagnement-titre">
      <div className="accompagnement__label" id="accompagnement-titre">
        Accompagnement
      </div>
      <div className="accompagnement__valeur">
        {nomAccompagnement(so)}
        {corrections.length > 0 && (
          <Pill tone="info" icon="fa-pen">
            Corrigée
          </Pill>
        )}
      </div>
      <p className="accompagnement__ligne">{ligneAccompagnement(so)}</p>
      <details className="accompagnement__pourquoi">
        <summary>
          <i className="fa fa-chevron-right" aria-hidden="true" />
          Pourquoi ?
        </summary>
        {so.version && <p className="accompagnement__version">{so.version.libelle}</p>}
        <ul className="accompagnement__etapes">
          {etapesDecision(so).map((etape) => (
            <li key={etape.texte} className={`accompagnement__etape accompagnement__etape--${etape.etat}`}>
              <i className={`fa ${ICONES[etape.etat]}`} aria-hidden="true" />
              <span>{etape.texte}</span>
            </li>
          ))}
        </ul>
        {corrections.length > 0 && (
          <>
            <p className="accompagnement__sous-titre">Décision automatique : {so.decision.operateur ? so.decision.operateur.nom : "Caf"}</p>
            <ul className="accompagnement__corrections">
              {corrections.map((ligne, i) => (
                // Deux corrections peuvent avoir le même texte ; la liste ne fait que s'allonger.
                <li key={i}>{ligne}</li>
              ))}
            </ul>
          </>
        )}
      </details>
      {so.choix && so.choix.length > 0 && (
        <button type="button" className="btn btn-ghost btn-sm accompagnement__corriger" ref={refCorriger} onClick={() => setOuverte(true)}>
          Corriger
        </button>
      )}
      {ouverte && (
        <ModaleCorrection
          allocataireId={allocataireId}
          nomAllocataire={nomAllocataire}
          so={so}
          onFermer={fermer}
          onCorrigee={(relue) => {
            if (!monte.current) return;
            setSo(relue);
            fermer();
            onCorrigee(relue);
          }}
        />
      )}
    </section>
  );
};

export default EncartAccompagnement;
